import React, { useState, useEffect, useRef } from 'react';
import PrivacyModal from './components/PrivacyModal';
import { Helmet } from 'react-helmet-async';
const globalStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Space+Grotesk:wght@300;400;600&family=JetBrains+Mono:wght@400;700&display=swap');

  :root {
    --bg-color: #050505;
    --text-main: #d1d1d1;
    --accent: #ffffff;
    --dark-grey: #111111;
  }

  * { box-sizing: border-box; }

  body {
    background-color: var(--bg-color);
    color: var(--text-main);
    font-family: 'Space Grotesk', sans-serif;
    overflow-x: hidden;
    margin: 0;
  }

  .font-display { font-family: 'Bebas Neue', sans-serif; letter-spacing: 0.05em; }
  .font-mono { font-family: 'JetBrains Mono', monospace !important; }

  .noise-overlay {
    position: fixed; top: 0; left: 0; width: 100vw; height: 100vh;
    pointer-events: none; z-index: 50; opacity: 0.03;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E");
  }

  .glitch-text { position: relative; color: white; display: inline-block; }
  .glitch-text::before, .glitch-text::after {
    content: attr(data-text); position: absolute; top: 0; left: 0;
    width: 100%; height: 100%; background: var(--bg-color);
  }
  .glitch-text::before {
    left: 2px; text-shadow: -1px 0 #888; clip: rect(24px, 550px, 90px, 0);
    animation: glitch-anim-2 3s infinite linear alternate-reverse;
  }
  .glitch-text::after {
    left: -2px; text-shadow: -1px 0 #fff; clip: rect(85px, 550px, 140px, 0);
    animation: glitch-anim 2.5s infinite linear alternate-reverse;
  }
  @keyframes glitch-anim {
    0% { clip: rect(10px, 9999px, 44px, 0); } 20% { clip: rect(48px, 9999px, 34px, 0); }
    40% { clip: rect(18px, 9999px, 83px, 0); } 60% { clip: rect(81px, 9999px, 75px, 0); }
    80% { clip: rect(64px, 9999px, 19px, 0); } 100% { clip: rect(12px, 9999px, 54px, 0); }
  }
  @keyframes glitch-anim-2 {
    0% { clip: rect(65px, 9999px, 100px, 0); } 20% { clip: rect(57px, 9999px, 26px, 0); }
    40% { clip: rect(38px, 9999px, 17px, 0); } 60% { clip: rect(42px, 9999px, 71px, 0); }
    80% { clip: rect(33px, 9999px, 81px, 0); } 100% { clip: rect(88px, 9999px, 12px, 0); }
  }

  .cursor-blink { animation: blink 1.2s step-end infinite; }
  @keyframes blink { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }

  .product-blur {
    filter: blur(6px) grayscale(100%) brightness(0.5);
    transition: all 0.8s ease-out; transform: scale(1.05);
  }
  .product-container:hover .product-blur {
    filter: blur(2px) grayscale(80%) brightness(0.7); transform: scale(1);
  }

  @media (pointer: fine) {
    body { cursor: none; }
    a, button, input { cursor: none; }
  }
  .cursor-dot {
    position: fixed; top: 0; left: 0; width: 4px; height: 4px;
    background: white; border-radius: 50%; pointer-events: none;
    z-index: 9999; transform: translate(-50%, -50%);
  }
  .cursor-ring {
    position: fixed; top: 0; left: 0; width: 30px; height: 30px;
    border: 1px solid rgba(255,255,255,0.4); border-radius: 50%;
    pointer-events: none; z-index: 9998; transform: translate(-50%, -50%);
    transition: width 0.2s, height 0.2s, border-color 0.2s, background-color 0.2s;
  }
  .cursor-hover { width: 50px !important; height: 50px !important; border-color: rgba(255,255,255,1) !important; background: rgba(255,255,255,0.05); }

  /* Faster hero logo */
  .hero-logo { animation: slowPulse 3s infinite alternate ease-in-out; }
  @keyframes slowPulse {
    0% { opacity: 0.05; transform: translate(-50%, -50%) scale(1); }
    100% { opacity: 0.12; transform: translate(-50%, -50%) scale(1.03); }
  }

  @keyframes scanDown { 0% { top: -10%; } 100% { top: 110%; } }
  .scan-line {
    position: absolute; left: 0; width: 100%; height: 2px;
    background: rgba(255,255,255,0.07); pointer-events: none;
    animation: scanDown 2.5s linear infinite;
  }

  @keyframes fadeInLine {
    from { opacity: 0; transform: translateX(-8px); }
    to { opacity: 1; transform: translateX(0); }
  }
  .boot-line { opacity: 0; animation: fadeInLine 0.3s forwards; }

  @keyframes pulseRing {
    0% { transform: scale(1); opacity: 0.6; }
    100% { transform: scale(1.8); opacity: 0; }
  }
  .pulse-ring {
    position: absolute; inset: -12px; border-radius: 50%;
    border: 1px solid rgba(255,255,255,0.3);
    animation: pulseRing 2s ease-out infinite;
  }

  .countdown-digit {
    font-family: 'JetBrains Mono', monospace;
    font-size: clamp(2rem, 6vw, 4rem);
    color: white; font-weight: 700; line-height: 1;
  }
  .countdown-label {
    font-family: 'JetBrains Mono', monospace;
    font-size: 9px; color: #444; letter-spacing: 0.2em; text-transform: uppercase; margin-top: 4px;
  }

  /* Audio button */
  .audio-btn {
    position: fixed; bottom: 24px; right: 24px;
    width: 40px; height: 40px; border: 1px solid #222;
    background: rgba(0,0,0,0.9); color: #555;
    display: flex; align-items: center; justify-content: center;
    font-size: 10px; letter-spacing: 0.1em; z-index: 200;
    transition: all 0.3s; font-family: 'JetBrains Mono', monospace;
    pointer-events: auto;
  }
  .audio-btn:hover { border-color: #fff; color: #fff; }
  .audio-btn.playing { border-color: #fff; color: #fff; }

  /* Manifesto lines reveal */
  @keyframes revealLine {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .reveal-line {
    opacity: 0; animation: revealLine 0.6s forwards;
  }

  @media (max-width: 768px) { .hide-mobile { display: none !important; } }
`;

const Entrance = ({ onAccess }) => {
  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-[#050505] text-white overflow-hidden">
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:40px_40px] pointer-events-none opacity-30"></div>
      <div className="scan-line"></div>

      <div className="z-10 flex flex-col items-center gap-12">
        <img src="/IMG_0349.jpeg" alt="SHADE Logo" className="w-16 h-16 md:w-20 md:h-20 object-contain filter invert opacity-60" />

        <button
          onClick={onAccess}
          className="group relative px-16 py-8 bg-white text-black hover:bg-gray-300 transition-all duration-300 flex flex-col items-center gap-2"
          style={{ cursor: 'pointer' }}
        >
          <span className="font-display text-5xl md:text-7xl tracking-[0.2em] uppercase leading-none">
            ENTER
          </span>
          <span className="font-mono text-[10px] tracking-widest text-[#444] font-bold uppercase mt-2 group-hover:text-black transition-colors duration-300">
            ACCESS THE NETWORK
          </span>
          
          {/* Techniczne detale narożników */}
          <div className="absolute top-1 left-1 w-2 h-2 border-t-2 border-l-2 border-black"></div>
          <div className="absolute top-1 right-1 w-2 h-2 border-t-2 border-r-2 border-black"></div>
          <div className="absolute bottom-1 left-1 w-2 h-2 border-b-2 border-l-2 border-black"></div>
          <div className="absolute bottom-1 right-1 w-2 h-2 border-b-2 border-r-2 border-black"></div>
        </button>
      </div>

      <div className="absolute bottom-8 font-mono text-[10px] text-gray-600 uppercase tracking-widest animate-pulse pointer-events-none">
        Click to initiate sequence
      </div>
    </div>
  );
};

const NavBar = ({ memberCount }) => (
  <nav className="fixed top-0 w-full px-6 py-4 flex justify-between items-center z-40 mix-blend-difference pointer-events-none">
    <div className="w-8 pointer-events-auto">
      <img src="/IMG_0349.jpeg" alt="SHADE" className="w-full filter invert" />
    </div>
    <div className="flex items-center gap-4 md:gap-8">
      <span className="font-mono text-[10px] text-white opacity-40 hidden md:block">
        {memberCount} 
      </span>
      <div className="font-mono text-[10px] text-white uppercase opacity-50 tracking-widest">
        SHADE MODE
      </div>
    </div>
  </nav>
);

const DROP_DATE = new Date('2025-09-01T00:00:00');

const Countdown = () => {
  const [time, setTime] = useState({ d: 0, h: 0, m: 0, s: 0 });

  useEffect(() => {
    const tick = () => {
      const now = new Date();
      const diff = Math.max(0, DROP_DATE - now);
      setTime({
        d: Math.floor(diff / 86400000),
        h: Math.floor((diff % 86400000) / 3600000),
        m: Math.floor((diff % 3600000) / 60000),
        s: Math.floor((diff % 60000) / 1000),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  const pad = (n) => String(n).padStart(2, '0');

  return (
    <div className="border border-[#1a1a1a] bg-[#080808] p-6 md:p-8 w-full max-w-lg">
      <p className="font-mono text-[10px] text-[#333] tracking-[0.3em] uppercase mb-6">DROP 01 — TIME REMAINING</p>
      <div className="flex gap-4 md:gap-8 items-start">
        {[['DAYS', pad(time.d)], ['HRS', pad(time.h)], ['MIN', pad(time.m)], ['SEC', pad(time.s)]].map(([label, val]) => (
          <div key={label} className="flex flex-col items-center">
            <span className="countdown-digit">{val}</span>
            <span className="countdown-label">{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const Hero = () => (
  <section className="relative h-screen flex flex-col items-center justify-center border-b border-[#111] overflow-hidden">
    <div className="absolute inset-0 bg-black flex items-center justify-center">
      <div className="w-full h-full bg-[radial-gradient(circle_at_center,rgba(20,20,20,0.8)_0%,rgba(0,0,0,1)_80%)]"></div>
    </div>
    <img
      src="/IMG_0349.jpeg"
      alt=""
      className="hero-logo absolute top-1/2 left-1/2 w-[120vw] md:w-[80vw] max-w-[1200px] object-contain filter invert pointer-events-none mix-blend-screen"
    />
    <div className="z-10 text-center flex flex-col items-center px-4 w-full mt-12">
      <h1
        className="font-display text-5xl md:text-8xl lg:text-[9rem] font-bold uppercase tracking-wide text-white mb-6 glitch-text leading-none"
        data-text="WE ARE THE ANOMALY."
      >
        WE ARE THE ANOMALY.
      </h1>
      <p className="text-gray-400 text-xs md:text-sm tracking-[0.2em] uppercase max-w-xl leading-relaxed font-mono">
        Not another brand. Not another trend.<br />
        A uniform for those who refuse to conform.
      </p>
      {/* <div className="mt-12 w-full flex justify-center">
        <Countdown />
      </div> */}
      <div className="absolute bottom-12 font-mono text-gray-700 text-xs tracking-widest uppercase animate-bounce">
        scroll
      </div>
    </div>
  </section>
);

const StatusBar = () => {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 2000);
    return () => clearInterval(id);
  }, []);
  const packets = (tick * 17 + 844).toString();

  return (
    <div className="w-full bg-[#080808] border-y border-[#111] px-6 py-3 flex flex-wrap gap-4 md:gap-8 font-mono text-[10px] text-[#333] overflow-hidden">
      <span>STATUS: <span className="text-green-700">ACTIVE</span></span>
  
      <span className="hide-mobile">OPERATING SINCE: <span className="text-[#444]">2026</span></span>
      <span className="hide-mobile">PACKETS_SENT: <span className="text-[#444]">{packets}</span></span>
      <span className="hide-mobile">LOCATION: <span className="text-[#444]">UKNNOWN</span></span>
      <span className="ml-auto"><span className="text-green-700 cursor-blink">■</span> ONLINE</span>
    </div>
  );
};

/* ── MERGED MANIFESTO + WHO WE ARE ── */
const ManifestoAndIdentity = () => (
  <section className="py-24 md:py-32 px-6 md:px-12 bg-[#030303] border-b border-[#111]">
    <div className="max-w-6xl mx-auto">

      {/* Top — big manifesto statements */}
      <div className="mb-20 border-l-2 border-[#1a1a1a] pl-6 md:pl-12">
        <p className="font-mono text-[10px] text-[#333] tracking-[0.4em] uppercase mb-10">SIGNAL TRANSMISSION</p>
        <div className="flex flex-col gap-4 mb-12">
          <p className="font-display text-3xl md:text-6xl text-gray-600 hover:text-white transition-colors duration-500 reveal-line" style={{ animationDelay: '0.1s' }}>
            THEY BUILT A WORLD
          </p>
          <p className="font-display text-3xl md:text-6xl text-gray-600 hover:text-white transition-colors duration-500 reveal-line" style={{ animationDelay: '0.2s' }}>
            DESIGNED TO EXPOSE YOU.
          </p>
          <div className="my-4 relative">
            <p className="font-display text-4xl md:text-7xl text-white glitch-text reveal-line" data-text="WE OPT OUT." style={{ animationDelay: '0.3s' }}>
              WE OPT OUT.
            </p>
          </div>
        </div>
      </div>

      {/* Bottom — identity grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">

        <div className="col-span-1 md:col-span-2">
          <div className="font-mono text-[10px] text-[#333] tracking-[0.3em] uppercase mb-6">ABOUT THE NETWORK</div>
          <h2 className="font-display text-3xl md:text-5xl text-white mb-6">
            NOT A BRAND.<br />A MOVEMENT.
          </h2>
          <p className="font-mono text-sm text-gray-500 leading-relaxed mb-4">
            The world is watching. Tracking. Cataloguing every move, every face, every choice. Surveillance dressed up as convenience. Control sold as connection.
          </p>
          <p className="font-mono text-sm text-gray-500 leading-relaxed mb-10">
            SHADE was built as a response. Not a brand for the loud. Not a uniform for the crowd. A signal for those who already know — that true freedom lives in anonymity. The micro-logo is not decoration. It is recognition between strangers who chose the same side.
          </p>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {[
              { label: 'FOUNDED', value: '2026' },
              { label: 'DROPS', value: '01' },
              { label: 'UNITS / DROP', value: '#50' },
            ].map(({ label, value }) => (
              <div key={label} className="border border-[#111] p-3 md:p-4 bg-[#070707] hover:border-[#333] transition-colors">
                <p className="font-mono text-[9px] text-[#333] tracking-widest uppercase mb-1">{label}</p>
                <p className="font-mono text-sm md:text-base text-white">{value}</p>
              </div>
            ))}
          </div>
        </div>

       
      </div>
    </div>
  </section>
);

const HoodieDropSection = ({ setMemberCount, memberCount }) => {
  const [email, setEmail] = useState('');
  const [joined, setJoined] = useState(false);
  const [error, setError] = useState(false); // Nowy stan dla błędu

  const handleJoin = async () => {
    // Solidny Regex sprawdzający format emaila
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    // Jeśli email jest poprawny, wykonaj Twój nowy kod API
    if (emailRegex.test(email)) {
      setError(false);
      try {
        const res = await fetch('/api/subscribe', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email }),
        });
        if (res.ok) {
          setJoined(true);
          setMemberCount(c => c + 1);
        }
      } catch (err) {
        console.error(err);
      }
    } else {
      // Jeśli email jest błędny, pokaż błąd wizualny
      setError(true);
    }
  };

  return (
    <section className="py-24 px-6 md:px-12 bg-black border-y border-[#111]">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row gap-12 items-center">
          <div className="w-full md:w-1/2 product-container relative overflow-hidden bg-[#0a0a0a] border border-[#222] cursor-crosshair">
            <div className="aspect-[4/5] relative flex items-center justify-center">
              <img src="/IMG_0349.jpeg19.png" alt="" className="absolute w-[80%] opacity-[0.03] filter invert pointer-events-none" />
              <img src="/IMG_0438.jpeg" alt="Classified Item" className="w-full h-full object-cover product-blur absolute inset-0" />
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
                <span className="text-white opacity-30 font-display tracking-[0.5em] text-xl border border-white/20 p-4 bg-black/40">
                  SOON
                </span>
              </div>
            </div>
            <div className="absolute top-4 left-4 font-mono text-[10px] text-gray-600 z-20">HOVER TO INSPECT</div>
            <div className="absolute bottom-4 right-4 font-mono text-[10px] text-[#222] z-20">FILE: REDACTED</div>
          </div>

          <div className="w-full md:w-1/2 flex flex-col">
            <div className="inline-block border border-[#333] px-3 py-1 text-xs text-white tracking-widest w-max mb-8 font-mono bg-[#111]">
              DROP 01 — COMING SOON
            </div>
            <h2 className="font-display text-3xl md:text-5xl text-white mb-4">THE ECLIPSE BALACLAVA HOODIE.</h2>
            <p className="text-gray-400 text-sm leading-relaxed mb-8 font-mono">
              The ultimate tool for anonymity. <span className="text-white font-bold">Heavyweight 400gsm French Terry cotton.</span>
              Features an integrated concealment balaclava designed to swallow your identity.
              Finished with a stealth micro-logo. You do not wear it to be seen.
            </p>
            <ul className="font-mono text-xs text-gray-500 tracking-widest flex flex-col gap-4 mb-12 border-l border-[#333] pl-4">
              <li><span className="text-white">— INTEGRATED BALACLAVA MASK</span></li>
              <li>— OVERSIZED, ANTI-FIT SILHOUETTE</li>
              <li>— STRICTLY LIMITED: UNDER 50 UNITS</li>
            </ul>

            <div className="bg-[#050505] border border-[#222] p-6">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-white text-lg tracking-widest font-display">SECURE YOUR ACCESS</h3>
                <span className="font-mono text-[10px] text-green-700 border border-green-900 px-2 py-1">
                  {memberCount} LISTED
                </span>
              </div>
              <p className="font-mono text-xs text-gray-500 mb-6">
                The drop goes public. But not for everyone at the same time. 30 minutes before launch — you receive an encrypted link and access code directly to your email. Early access. First pick. No waiting.
              </p>
              {!joined ? (
                <div className="flex flex-col gap-2">
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input
                      type="email"
                      placeholder="ENTER EMAIL"
                      value={email}
                      onChange={e => {
                        setEmail(e.target.value);
                        if (error) setError(false); // Ukrywa błąd, gdy użytkownik wpisuje nowy znak
                      }}
                      onKeyDown={e => e.key === 'Enter' && handleJoin()}
                      className={`bg-black border ${error ? 'border-red-900 focus:border-red-600' : 'border-[#333] focus:border-white'} text-white px-4 py-3 w-full outline-none text-xs transition-colors font-mono`}
                    />
                    <button
                      onClick={handleJoin}
                      className="bg-white text-black px-6 py-3 font-bold text-xs tracking-widest hover:bg-gray-300 transition-colors whitespace-nowrap font-mono"
                    >
                      JOIN THE LIST
                    </button>
                  </div>
                  
                  {/* Nowy mroczny komunikat o błędzie */}
                  {error && (
                    <p className="font-mono text-[10px] text-red-600 tracking-widest uppercase mt-1 animate-pulse">
                      &gt; INVALID DATA STREAM
                    </p>
                  )}
                </div>
              ) : (
                <div className="border border-green-900 bg-[#0a0f0a] p-4 font-mono text-xs text-green-700 tracking-widest">
                  ACCESS GRANTED. YOU ARE NOW SHADE.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
  
const Footer = () => {
  const [showPrivacy, setShowPrivacy] = useState(false);

  return (
    <>
      {showPrivacy && <PrivacyModal onClose={() => setShowPrivacy(false)} />}
      
      <footer className="relative py-16 px-6 md:px-12 text-center bg-[#020202] border-t border-[#0a0a0a]">
        
        {/* Logo */}
        <img 
          src="/IMG_0349.jpeg" 
          alt="SHADE" 
          className="w-10 mx-auto mb-10 filter invert opacity-20 hover:opacity-100 transition-opacity duration-500" 
        />

        {/* Główne linki w jednej linii */}
        <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16 mb-12 font-mono text-[10px] tracking-[0.2em] text-gray-500">
          <a href="https://instagram.com/betheshade" target="_blank" rel="noreferrer" className="hover:text-white transition-colors duration-300">
            INSTAGRAM
          </a>
          <a href="https://tiktok.com/@besh4de" target="_blank" rel="noreferrer" className="hover:text-white transition-colors duration-300">
            TIKTOK
          </a>
          <button onClick={() => setShowPrivacy(true)} className="hover:text-white transition-colors bg-transparent border-0 p-0 font-mono text-[10px] tracking-[0.2em] text-gray-500 uppercase">
            PRIVACY POLICY
          </button>
        </div>

        {/* Minimalistyczny podpis terminalowy z pulsującą diodą */}
        <div className="flex flex-col items-center gap-2 font-mono text-[9px] tracking-[0.3em] text-[#333]">
          <div className="uppercase flex items-center gap-2">
            <span className="w-1 h-1 bg-[#444] rounded-full animate-pulse"></span>
            2026 SHADE. EMBRACE THE DARK.
          </div>
          <div className="opacity-50">
            NODE: UNKNOWN — STATUS: ACTIVE
          </div>
        </div>

      </footer>
    </>
  );
};

/* ── AUDIO BUTTON ── */
const AudioButton = ({ audioRef }) => {
  const [playing, setPlaying] = useState(false);

  const toggle = () => {
    if (!audioRef.current) return;
    if (playing) {
      audioRef.current.pause();
      setPlaying(false);
    } else {
      audioRef.current.play();
      setPlaying(true);
    }
  };

  return (
    <button onClick={toggle} className={`audio-btn ${playing ? 'playing' : ''}`} title="Toggle ambient sound">
      {playing ? 'SND ON' : 'SND OFF'}
    </button>
  );
};

const CONSENT_KEY = "shade_consent_v1";

const defaultConsent = {
  analytics: false,
  marketing: false,
};

export const CookieBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [consent, setConsent] = useState(defaultConsent);

  useEffect(() => {
    const saved = localStorage.getItem(CONSENT_KEY);

    if (!saved) {
      const timer = setTimeout(() => setIsVisible(true), 1500);
      return () => clearTimeout(timer);
    } else {
      const parsed = JSON.parse(saved);
      applyConsentToGTM(parsed);
    }
  }, []);

  // Tutaj jest kluczowa zmiana: nie ładujemy skryptów, tylko wysyłamy sygnał do GTM
  const applyConsentToGTM = (consentState) => {
    window.dataLayer = window.dataLayer || [];
    
    // Ustawiamy domyślny tryb zgody dla Google (Consent Mode v2)
    function gtag(){window.dataLayer.push(arguments);}
    gtag('consent', 'update', {
      'analytics_storage': consentState.analytics ? 'granted' : 'denied',
      'ad_storage': consentState.marketing ? 'granted' : 'denied',
      'ad_user_data': consentState.marketing ? 'granted' : 'denied',
      'ad_personalization': consentState.marketing ? 'granted' : 'denied'
    });

    // Dodatkowy własny event dla innych tagów (np. Meta Pixel)
    if (consentState.marketing) {
       window.dataLayer.push({ event: 'marketing_consent_granted' });
    }
  };

  const saveConsent = (newConsent) => {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(newConsent));
    applyConsentToGTM(newConsent);
    setIsVisible(false);
  };

  const acceptAll = () => {
    const full = { analytics: true, marketing: true };
    saveConsent(full);
  };

  const declineAll = () => {
    saveConsent(defaultConsent);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 w-full bg-[#050505] border-t border-[#222] p-4 md:p-6 pr-20 md:pr-24 z-[100] text-gray-400 font-mono shadow-[0_-10px_40px_rgba(0,0,0,0.8)]">

      {!showSettings ? (
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 max-w-7xl mx-auto">

          <div className="max-w-3xl">
            <div className="text-white text-[10px] tracking-[0.2em] mb-2 uppercase flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-red-600 rounded-full animate-pulse"></span>
              SYS.MSG // TRACE PROTOCOLS REQUIRED
            </div>
            <p className="text-[10px] tracking-[0.2em] uppercase leading-relaxed">
              We despise the System, but we use its tools to locate anomalies like you. 
              By proceeding, you accept our tracking protocols. 
              <br />
              <button onClick={() => setShowSettings(true)} className="underline text-gray-300 hover:text-white mt-2">
                Configure Protocols
              </button>
            </p>
          </div>

          <div className="flex gap-4 shrink-0 mt-4 lg:mt-0 w-full lg:w-auto">
            <button
              onClick={declineAll}
              className="flex-1 lg:flex-none border border-[#333] px-4 py-2 text-[10px] tracking-widest hover:border-gray-400 hover:text-white transition-colors uppercase"
            >
              STAY DARK
            </button>
            <button
              onClick={acceptAll}
              className="flex-1 lg:flex-none bg-white text-black px-6 py-2 text-[10px] font-bold tracking-widest hover:bg-gray-300 transition-colors uppercase"
            >
              ACCEPT TRACE
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-6 max-w-3xl mx-auto w-full">
          <div>
            <h3 className="text-white text-[11px] mb-4 uppercase tracking-widest border-b border-[#222] pb-2">
              Select Protocols
            </h3>

            <div className="flex flex-col gap-4 text-[10px] uppercase tracking-widest">
              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={consent.analytics}
                  onChange={(e) => setConsent({ ...consent, analytics: e.target.checked })}
                  className="mt-0.5 accent-white bg-[#111] border-[#333]"
                />
                <span className="group-hover:text-white transition-colors">
                  <strong className="text-white">Analytics_</strong> Traffic measurement and behavior tracking.
                </span>
              </label>

              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={consent.marketing}
                  onChange={(e) => setConsent({ ...consent, marketing: e.target.checked })}
                  className="mt-0.5 accent-white bg-[#111] border-[#333]"
                />
                <span className="group-hover:text-white transition-colors">
                  <strong className="text-white">Marketing_</strong> Cross-network tracking for targeted recruitment (Ads).
                </span>
              </label>
            </div>
          </div>

          <div className="flex gap-4">
            <button
              onClick={() => setShowSettings(false)}
              className="border border-[#333] px-4 py-2 text-[10px] tracking-widest hover:border-gray-400 hover:text-white transition-colors uppercase"
            >
              BACK
            </button>
            <button
              onClick={() => saveConsent(consent)}
              className="bg-white text-black px-6 py-2 text-[10px] font-bold tracking-widest hover:bg-gray-300 transition-colors uppercase"
            >
              SAVE CONFIGURATION
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/* ── STICKY DROP BUTTON ── */
const StickyDropButton = ({ onOpen }) => { // <--- Dodajemy onOpen
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      // Przycisk pojawia się dopiero po przescrollowaniu połowy pierwszego ekranu (Hero)
      if (window.scrollY > window.innerHeight * 0.5) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToDrop = () => {
    document.getElementById('drop-zone')?.scrollIntoView({ behavior: 'smooth' });
  };

  // Jeśli jesteśmy na samej górze, przycisk jest niewidoczny
  if (!isVisible) return null;

  return (
    <button
      onClick={onOpen} // <--- Podpinamy onOpen
      className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[80] bg-[#050505] text-white border border-[#333] px-6 py-3 font-mono text-[10px] tracking-[0.3em] uppercase shadow-[0_0_20px_rgba(0,0,0,0.8)] hover:bg-white hover:text-black hover:border-white transition-all duration-300 flex items-center gap-3 group"
    >
      <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse group-hover:bg-black"></span>
      <span className="whitespace-nowrap">JOIN THE DROP</span>
    </button>
  );
};

/* ── DROP MODAL (POP-UP) ── */
const DropModal = ({ onClose, memberCount, setMemberCount }) => {
  const [email, setEmail] = useState('');
  const [joined, setJoined] = useState(false);
  const [error, setError] = useState(false);

  const handleJoin = async () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (emailRegex.test(email)) {
      setError(false);
      try {
        const res = await fetch('/api/subscribe', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email }),
        });
        if (res.ok) {
          setJoined(true);
          setMemberCount(c => c + 1);
        }
      } catch (err) {
        console.error(err);
      }
    } else {
      setError(true);
    }
  };

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-black/90 backdrop-blur-md">
      <div className="relative w-full max-w-lg bg-[#050505] border border-[#222] shadow-[0_0_50px_rgba(0,0,0,0.9)]">
        
        {/* Header Modala */}
        <div className="p-4 border-b border-[#222] flex justify-between items-center bg-[#0a0a0a]">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
            <h2 className="font-mono text-xs tracking-[0.3em] text-white uppercase">Access_Terminal</h2>
          </div>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-white transition-colors font-mono text-xs tracking-widest"
          >
            [ CLOSE ]
          </button>
        </div>

        {/* Zawartość Modala */}
        <div className="p-6 md:p-8">
          <div className="flex justify-between items-start mb-4">
            <h3 className="text-white text-xl tracking-widest font-display uppercase">Secure Your Access</h3>
            <span className="font-mono text-[10px] text-green-700 border border-green-900 px-2 py-1">
              {memberCount} LISTED
            </span>
          </div>
          <p className="font-mono text-xs text-gray-500 mb-8 leading-relaxed">
            The drop goes public. But not for everyone at the same time. 30 minutes before launch — you receive an encrypted link and access code directly to your email. Early access. First pick. No waiting.
          </p>

          {!joined ? (
            <div className="flex flex-col gap-2">
              <div className="flex flex-col gap-3">
                <input
                  type="email"
                  placeholder="ENTER EMAIL"
                  value={email}
                  onChange={e => {
                    setEmail(e.target.value);
                    if (error) setError(false);
                  }}
                  onKeyDown={e => e.key === 'Enter' && handleJoin()}
                  className={`bg-black border ${error ? 'border-red-900 focus:border-red-600' : 'border-[#333] focus:border-white'} text-white px-4 py-4 w-full outline-none text-xs transition-colors font-mono`}
                />
                <button
                  onClick={handleJoin}
                  className="bg-white text-black px-6 py-4 font-bold text-xs tracking-widest hover:bg-gray-300 transition-colors uppercase font-mono w-full"
                >
                  JOIN THE LIST
                </button>
              </div>
              
              {error && (
                <p className="font-mono text-[10px] text-red-600 tracking-widest uppercase mt-2 animate-pulse text-center">
                  &gt; INVALID DATA STREAM
                </p>
              )}
            </div>
          ) : (
            <div className="border border-green-900 bg-[#0a0f0a] p-6 font-mono text-xs text-green-700 tracking-widest text-center flex flex-col gap-2">
              <span>ACCESS GRANTED.</span>
              <span>YOU ARE NOW SHADE.</span>
              <button onClick={onClose} className="mt-4 text-white underline text-[10px] hover:text-gray-400">
                CLOSE TERMINAL
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default function App() {
  const [hasAccess, setHasAccess] = useState(false);
  const [opacity, setOpacity] = useState(0);
   const [lang, setLang] = useState('en');
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [isHovering, setIsHovering] = useState(false);
  const [memberCount, setMemberCount] = useState(247);
  const [showDropModal, setShowDropModal] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    if (hasAccess) {
      setTimeout(() => setOpacity(1), 100);
      if (audioRef.current) {
        audioRef.current.volume = 0.3;
        audioRef.current.play().catch(() => {});
      }
    }
  }, [hasAccess]);

  useEffect(() => {
    const handleMouseMove = (e) => setMousePos({ x: e.clientX, y: e.clientY });
    const handleMouseOver = (e) => {
      setIsHovering(
        e.target.tagName.toLowerCase() === 'button' ||
        e.target.tagName.toLowerCase() === 'a' ||
        e.target.tagName.toLowerCase() === 'input' ||
        !!e.target.closest('.hover-trigger')
      );
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseover', handleMouseOver);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseover', handleMouseOver);
    };
  }, []);

  return (
    <>
      <style>{globalStyles}</style>
      <audio ref={audioRef} src="/cyberpunk.mp3" loop />
      <div className="cursor-dot hidden md:block" style={{ left: mousePos.x, top: mousePos.y }}></div>
      <div className={`cursor-ring hidden md:block ${isHovering ? 'cursor-hover' : ''}`} style={{ left: mousePos.x, top: mousePos.y }}></div>
      <div className="noise-overlay"></div>

      {!hasAccess ? (
        <Entrance onAccess={() => setHasAccess(true)} />
      ) : (
        <div className="transition-opacity duration-1000 ease-in-out bg-[#050505]" style={{ opacity }}>
          {/* --- TUTAJ WKLEJASZ HELMET --- */}
        <Helmet>
          <title>{lang === 'en' ? 'SHADE | The Anomaly' : 'SHADE | Anomalia'}</title>
          <meta name="description" content={lang === 'en' ? 'Premium classified gear. Opt out of the ordinary.' : 'Ekskluzywny sprzęt taktyczny. Wyrwij się ze schematu.'} />
        </Helmet>
        {/* ----------------------------- */}
          <NavBar memberCount={memberCount} lang={lang} setLang={setLang} />
          <Hero lang={lang} />
          <StatusBar />
          <ManifestoAndIdentity />
          <HoodieDropSection memberCount={memberCount} setMemberCount={setMemberCount} />
       <Footer />
          <AudioButton audioRef={audioRef} />
          
          <StickyDropButton onOpen={() => setShowDropModal(true)} /> 
          
          {showDropModal && (
            <DropModal 
              onClose={() => setShowDropModal(false)} 
              memberCount={memberCount} 
              setMemberCount={setMemberCount} 
            />
          )}

          <CookieBanner />
        </div>
      )}
    </>
  );
}